/**
 * 项目申报书编写 —— 第 0 步：立项（项目基础信息 + 申报要素 + 申报文件）
 *
 * 与投标文件的「标书解析」不同：申报书不是解析招标文件，而是录入
 * 项目题目、核心创新点及申报要素，并上传申报指南/通知文件与申报模板，
 * 这些要素会注入后续目录与正文生成。
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CloudArrowUpIcon,
  DocumentTextIcon,
  BookOpenIcon,
  CheckCircleIcon,
  TrashIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { projectApi, documentApi, knowledgeApi, getErrorMessage } from '../services/api';
import type { ProjectInfo, AppMeta, KnowledgeBaseFile } from '../types';

interface ProjectAppSetupProps {
  project: ProjectInfo;
  onSaved: (project: ProjectInfo) => void;
  readOnly?: boolean;
}

const EMPTY_META: AppMeta = {
  app_type: '',
  app_field: '',
  app_keywords: '',
  app_budget: '',
  app_period: '',
  app_leader: '',
  app_team: '',
  app_support_unit: '',
};

const ProjectAppSetup: React.FC<ProjectAppSetupProps> = ({ project, onSaved, readOnly = false }) => {
  const [name, setName] = useState(project.name || '');
  const [innovationPoints, setInnovationPoints] = useState(project.innovation_points || '');
  const [meta, setMeta] = useState<AppMeta>({ ...EMPTY_META, ...(project.app_meta || {}) });
  const [guideName, setGuideName] = useState(project.app_guide_name || '');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingGuide, setUploadingGuide] = useState(false);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [templateFiles, setTemplateFiles] = useState<KnowledgeBaseFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const guideInputRef = useRef<HTMLInputElement>(null);
  const templateInputRef = useRef<HTMLInputElement>(null);

  // 申报模板存放的知识库：取项目关联的第一个知识库
  const templateKbId = project.knowledge_base_ids?.[0] ?? null;

  const loadTemplateFiles = useCallback(async (kbId: number | null) => {
    if (!kbId) {
      setTemplateFiles([]);
      return;
    }
    setLoadingFiles(true);
    try {
      const res = await knowledgeApi.listFiles(kbId);
      setTemplateFiles(res.data.files || []);
    } catch {
      setTemplateFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  }, []);

  useEffect(() => {
    void loadTemplateFiles(templateKbId);
  }, [templateKbId, loadTemplateFiles]);

  const setMetaField = (key: keyof AppMeta, value: string) => {
    setMeta((prev) => ({ ...prev, [key]: value }));
  };

  // AI 生成核心创新点（基于项目题目）
  const handleAiGenerateInnovation = async () => {
    if (!name.trim()) {
      setMessage({ type: 'error', text: '请先填写项目题目' });
      return;
    }
    setAiGenerating(true);
    setMessage(null);
    setInnovationPoints('');
    try {
      const full = await projectApi.generateInnovationPointsStream(
        project.id,
        name.trim(),
        project.description || undefined,
        (fullText) => setInnovationPoints(fullText),
      );
      setInnovationPoints(full.trim());
      setMessage({ type: 'success', text: '已生成核心创新点，可继续编辑后保存' });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, 'AI 生成失败') });
    } finally {
      setAiGenerating(false);
    }
  };

  const save = async () => {
    if (!name.trim()) {
      setMessage({ type: 'error', text: '请填写项目题目' });
      return;
    }
    try {
      setSaving(true);
      setMessage(null);
      const res = await projectApi.update(project.id, {
        name: name.trim(),
        innovation_points: innovationPoints,
        app_meta: meta,
        app_guide_name: guideName,
      });
      onSaved(res.data);
      setMessage({ type: 'success', text: '立项信息已保存' });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '保存失败') });
    } finally {
      setSaving(false);
    }
  };

  // 上传申报指南/通知文件：提取文本后直接存到项目 app_guide_text
  const handleGuideUpload = async (file: File) => {
    try {
      setUploadingGuide(true);
      setMessage(null);
      const res = await documentApi.uploadFileWithProject(file, project.id);
      if (res.data.success && res.data.file_content) {
        const updated = await projectApi.update(project.id, {
          app_guide_text: res.data.file_content,
          app_guide_name: file.name,
        });
        onSaved(updated.data);
        setGuideName(file.name);
        setMessage({ type: 'success', text: `申报指南「${file.name}」已上传并解析` });
      } else {
        setMessage({ type: 'error', text: res.data.message || '文件解析失败' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '申报指南上传失败') });
    } finally {
      setUploadingGuide(false);
    }
  };

  // 上传申报模板到知识库：作为体例参考参与目录/正文生成（支持多文件）
  const handleTemplateUpload = async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (!list.length) return;
    try {
      setUploadingTemplate(true);
      setMessage(null);
      let kbId = templateKbId;
      if (!kbId) {
        const kb = await knowledgeApi.create({ name: `【申报模板】${project.name}` });
        kbId = kb.data.id;
        const updated = await projectApi.update(project.id, {
          knowledge_base_ids: [kbId, ...(project.knowledge_base_ids || [])],
        });
        onSaved(updated.data);
      }
      await knowledgeApi.uploadFiles(kbId, list);
      await loadTemplateFiles(kbId);
      setMessage({ type: 'success', text: `已上传 ${list.length} 个申报模板到知识库` });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '申报模板上传失败') });
    } finally {
      setUploadingTemplate(false);
    }
  };

  const handleTemplateDelete = async (fileId: number) => {
    if (!templateKbId) return;
    if (!window.confirm('确定删除该申报模板吗？')) return;
    try {
      await knowledgeApi.deleteFile(templateKbId, fileId);
      await loadTemplateFiles(templateKbId);
      setMessage({ type: 'success', text: '模板已删除' });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '删除失败') });
    }
  };

  const fieldCls =
    'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400';

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* 标题 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-1">📋 项目立项</h2>
        <p className="text-sm text-gray-500">
          填写项目题目、核心创新点与申报要素，可上传申报指南/通知文件与申报模板，这些信息将用于生成目录与正文。
        </p>
      </div>

      {/* 项目基本信息 */}
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <h3 className="text-sm font-semibold text-gray-800">项目基本信息</h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">项目题目</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={readOnly}
            className={fieldCls}
            placeholder="请输入项目题目"
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700">核心创新点</label>
            <button
              type="button"
              onClick={() => void handleAiGenerateInnovation()}
              disabled={readOnly || aiGenerating}
              className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md text-purple-700 bg-purple-50 hover:bg-purple-100 disabled:opacity-50"
              title="AI 根据项目题目生成核心创新点"
            >
              <SparklesIcon className="w-3.5 h-3.5 mr-1" />
              {aiGenerating ? '生成中...' : 'AI 生成'}
            </button>
          </div>
          <textarea
            value={innovationPoints}
            onChange={(e) => setInnovationPoints(e.target.value)}
            rows={4}
            disabled={readOnly}
            className={fieldCls}
            placeholder="本项目申报的核心创新点，将用于目录与正文生成"
          />
        </div>
      </div>

      {/* 申报要素 */}
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <h3 className="text-sm font-semibold text-gray-800">申报要素</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">申报类型</label>
            <input
              type="text"
              value={meta.app_type || ''}
              onChange={(e) => setMetaField('app_type', e.target.value)}
              disabled={readOnly}
              className={fieldCls}
              placeholder="如：科技计划 / 自然科学基金 / 重点研发"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">研究领域</label>
            <input
              type="text"
              value={meta.app_field || ''}
              onChange={(e) => setMetaField('app_field', e.target.value)}
              disabled={readOnly}
              className={fieldCls}
              placeholder="所属领域 / 学科方向"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">关键词</label>
            <input
              type="text"
              value={meta.app_keywords || ''}
              onChange={(e) => setMetaField('app_keywords', e.target.value)}
              disabled={readOnly}
              className={fieldCls}
              placeholder="多个关键词用顿号或逗号分隔"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">经费预算</label>
            <input
              type="text"
              value={meta.app_budget || ''}
              onChange={(e) => setMetaField('app_budget', e.target.value)}
              disabled={readOnly}
              className={fieldCls}
              placeholder="如：200 万元"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">研究期限</label>
            <input
              type="text"
              value={meta.app_period || ''}
              onChange={(e) => setMetaField('app_period', e.target.value)}
              disabled={readOnly}
              className={fieldCls}
              placeholder="如：2026.01 - 2028.12"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">项目负责人</label>
            <input
              type="text"
              value={meta.app_leader || ''}
              onChange={(e) => setMetaField('app_leader', e.target.value)}
              disabled={readOnly}
              className={fieldCls}
              placeholder="项目负责人姓名"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">依托单位</label>
            <input
              type="text"
              value={meta.app_support_unit || ''}
              onChange={(e) => setMetaField('app_support_unit', e.target.value)}
              disabled={readOnly}
              className={fieldCls}
              placeholder="申报依托单位"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">团队成员</label>
            <textarea
              value={meta.app_team || ''}
              onChange={(e) => setMetaField('app_team', e.target.value)}
              rows={3}
              disabled={readOnly}
              className={fieldCls}
              placeholder="项目团队成员及其分工（可选）"
            />
          </div>
        </div>
      </div>

      {/* 申报文件 */}
      <div className="bg-white rounded-lg shadow p-6 space-y-5">
        <h3 className="text-sm font-semibold text-gray-800">申报文件</h3>

        {/* 申报指南 / 通知文件 */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <DocumentTextIcon className="w-5 h-5 text-blue-600" />
              <div>
                <div className="text-sm font-medium text-gray-800">申报指南 / 通知文件</div>
                <div className="text-xs text-gray-400">
                  {guideName ? (
                    <span className="flex items-center gap-1 text-green-600">
                      <CheckCircleIcon className="w-3.5 h-3.5" /> 已上传：{guideName}
                    </span>
                  ) : (
                    '上传官方申报通知/指南，作为要求与体例参考'
                  )}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => guideInputRef.current?.click()}
              disabled={readOnly || uploadingGuide}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50"
            >
              <CloudArrowUpIcon className="w-4 h-4 mr-1" />
              {uploadingGuide ? '解析中...' : guideName ? '重新上传' : '上传文件'}
            </button>
            <input
              ref={guideInputRef}
              type="file"
              accept=".pdf,.docx,.doc"
              className="hidden"
              disabled={readOnly}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleGuideUpload(f);
                e.target.value = '';
              }}
            />
          </div>
        </div>

        {/* 申报模板 → 知识库 */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <BookOpenIcon className="w-5 h-5 text-purple-600" />
              <div>
                <div className="text-sm font-medium text-gray-800">申报模板（进知识库）</div>
                <div className="text-xs text-gray-400">
                  {templateFiles.length ? (
                    <span className="flex items-center gap-1 text-green-600">
                      <CheckCircleIcon className="w-3.5 h-3.5" /> 已上传 {templateFiles.length} 个模板
                    </span>
                  ) : (
                    '上传官方申报书模板，作为体例参考参与目录与正文生成（可上传多个）'
                  )}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => templateInputRef.current?.click()}
              disabled={readOnly || uploadingTemplate}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg text-purple-700 bg-purple-50 hover:bg-purple-100 disabled:opacity-50"
            >
              <CloudArrowUpIcon className="w-4 h-4 mr-1" />
              {uploadingTemplate ? '上传中...' : '上传模板'}
            </button>
            <input
              ref={templateInputRef}
              type="file"
              accept=".pdf,.docx,.doc,.xlsx,.xls"
              multiple
              className="hidden"
              disabled={readOnly}
              onChange={(e) => {
                const files = e.target.files;
                if (files && files.length) void handleTemplateUpload(files);
                e.target.value = '';
              }}
            />
          </div>

          {/* 模板文件列表 */}
          {loadingFiles ? (
            <div className="mt-3 text-xs text-gray-400">加载模板列表...</div>
          ) : templateFiles.length > 0 ? (
            <ul className="mt-3 space-y-1.5">
              {templateFiles.map((f) => (
                <li
                  key={f.id}
                  className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-gray-50 text-sm"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <DocumentTextIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="truncate text-gray-700">{f.original_name}</span>
                  </span>
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => void handleTemplateDelete(f.id)}
                      className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50"
                      title="删除模板"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>

      {/* 保存 */}
      {!readOnly && (
        <div className="flex justify-end">
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center justify-center px-6 py-2.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {saving ? '保存中...' : '保存立项信息'}
          </button>
        </div>
      )}

      {/* 消息提示 */}
      {message && (
        <div
          className={`p-4 rounded-md ${
            message.type === 'success'
              ? 'bg-green-100 text-green-700 border border-green-200'
              : 'bg-red-100 text-red-700 border border-red-200'
          }`}
        >
          {message.text}
        </div>
      )}
    </div>
  );
};

export default ProjectAppSetup;
